import { useModel } from "@stem-sims/nexus"
import Pane from "components/Dashboards/General/Pane"
import FAIcon from "components/General/FAIcon"
import { Modal, Row, Col, Button, Form } from "react-bootstrap"
import { PulseLoader } from "react-spinners"
import { toast } from "react-toastify"
import { createImageFolder, deleteImage, getImages, uploadImage } from "shared/routes/curriculum/books"
import styles from "./editingStyles.module.scss"
import { useState } from "react"

const { selectable } = styles

type Props = {
    modal: string
    setModal: (modal: string) => void
}

const modals = ["imageLibrary", "newDirectory", "newImage"]

export default function ImageLibrary({ modal, setModal }: Props) {
    const [selectedPath, setSelectedPath] = useState("/")

    const { loading: imageLoading, response: root, refreshModel: refreshImages } = useModel({
        model: getImages,
        props: { relativePath: selectedPath },
    })

    const [references, setReferences] = useState<string[]>([])
    const [needDeleteConfirmation, setNeedDeleteConfirmation] = useState<boolean>(true)

    const [sortBy, setSortBy] = useState<"name" | "date">("date")

    return <>
        <Modal size="xl" show={modal === "imageLibrary"} onHide={() => setModal(null)}>
            <Modal.Header closeButton>
                <span>
                    Image Library - {selectedPath}
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex flex-row justify-content-start mb-3">
                    <Button className="me-2" disabled={selectedPath === "/"}
                        onClick={() => {
                            setSelectedPath((old) => {
                                return old.slice(0, old.lastIndexOf("/", old.length - 2) + 1)
                            })
                        }}
                    ><FAIcon iconName="arrow-up" /> Parent</Button>
                    <Button className="me-2" onClick={() => { setModal("newDirectory") }}>
                        <FAIcon iconName="plus" /> Folder
                    </Button>
                    <Button className="me-2" onClick={() => { setModal("newImage") }}>
                        <FAIcon iconName="plus" /> Image
                    </Button>
                    <div className="d-flex flex-row justify-content-start ms-auto">
                        <Button className="me-2" variant={sortBy === "name" ? "primary" : "outline-primary"} onClick={() => setSortBy("name")}>
                            Sort by Name
                        </Button>
                        <Button className="me-2" variant={sortBy === "date" ? "primary" : "outline-primary"} onClick={() => setSortBy("date")}>
                            Sort by Date
                        </Button>
                    </div>
                </div>
                <hr className="my-0" />
                <Row style={{ maxHeight: "80vh", overflow: "scroll" }} className="d-flex justify-content-start">

                    {imageLoading && <>
                        <PulseLoader />
                    </>}

                    {!imageLoading && root && <>

                        {root.subdirectories.map((dir) => (
                            <Col key={dir} xs={3} className="my-2" onClick={() => { setSelectedPath((old) => `${old}${dir}/`) }}>
                                <Pane className={selectable}><FAIcon iconName="folder" /> {dir}</Pane>
                            </Col>
                        ))}

                        {root.files.sort((a, b) => {
                            if (sortBy === "name") {
                                return a.name.localeCompare(b.name)
                            } else {
                                return new Date(b.date).getTime() - new Date(a.date).getTime()
                            }
                        }).map((src, i) => (
                            <Col key={src.name + i} xs={3} className="my-2" onClick={() => {
                                setModal(src.name)
                            }}>
                                <Pane className={selectable}>
                                    <img alt="" style={{ width: "75%", height: "75%", objectFit: "contain", aspectRatio: "1/1" }} src={`/images/curriculum${selectedPath}${src.name}`} />
                                    <span className="text-center">{src.name}</span>
                                </Pane>
                            </Col>
                        ))}
                    </>}
                </Row>
            </Modal.Body>
        </Modal>

        <Modal show={modal === "newDirectory"} onHide={() => setModal(null)}>
            <Form onSubmit={(event) => {
                event.preventDefault()
                const name = event.currentTarget.elements["name"].value
                toast.info(`Making ${name}, I pwomise`)
                createImageFolder({ directory: selectedPath, newDir: name }).then(() => {
                    setModal("imageLibrary")
                    refreshImages()
                })
            }}>
                <Modal.Header closeButton>Make New Directory</Modal.Header>

                <Modal.Body>
                    <Form.Group controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control></Form.Control>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Row>
                        <Col>
                            <Button variant="theme" onClick={() => setModal("imageLibrary")}>Return</Button>
                        </Col>
                        <Col>
                            <Button variant="theme" type="submit">
                                Create
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Form>
        </Modal>

        <Modal show={modal === "newImage"} onHide={() => setModal(null)}>
            <Form onSubmit={(event) => {
                event.preventDefault()
                const image = event.currentTarget.elements["image"].files[0]
                uploadImage({ directory: selectedPath, image: image }).then((response) => {
                    navigator.clipboard.writeText(`<img src="/images/curriculum${selectedPath}${response.id}.png" />`).then(() => {
                        setModal("imageLibrary")
                        refreshImages()
                        toast.info("Copied img tag!", { autoClose: 1000 })
                    })
                })
            }}>
                <Modal.Header closeButton>Upload New Image</Modal.Header>

                <Modal.Body>
                    <Form.Group controlId="image">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="file" accept=".png,image/png,.jpg,.jpeg,image/jpeg"></Form.Control>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Row>
                        <Col>
                            <Button variant="theme" onClick={() => setModal("imageLibrary")}>Return</Button>
                        </Col>
                        <Col>
                            <Button variant="theme" type="submit">
                                Upload
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Form>
        </Modal>

        <Modal size="xl" show={modal && !modals.includes(modal)} onHide={() => {
            setModal(null)
            setReferences([])
            setNeedDeleteConfirmation(true)
        }}>
            <Modal.Header closeButton>Selected Image</Modal.Header>

            <Modal.Body>
                <img alt="" style={{ maxWidth: "80%", maxHeight: "80vh" }} src={`/images/curriculum${selectedPath}${modal}`} />
                {references.length > 0 && <>
                    <hr />
                    <h5 className="text-danger">References</h5>
                    <ul>
                        {references.map((reference) => <li key={reference}>{reference}</li>)}
                    </ul>
                </>}
            </Modal.Body>

            <Modal.Footer>
                <Row className="w-100 justify-content-end">
                    <Col xs={2}>
                        <Button variant="danger" onClick={() => {
                            deleteImage({ relativePath: selectedPath + modal, testDelete: needDeleteConfirmation }).then(() => {
                                if (!needDeleteConfirmation) {
                                    setModal("imageLibrary")
                                    refreshImages()
                                    setReferences([])
                                    setNeedDeleteConfirmation(true)
                                    toast.info("Deleted image!", { autoClose: 10000 })
                                } else {
                                    setNeedDeleteConfirmation(false)
                                }
                            }).catch((error) => {
                                toast.error("Failed to delete image!\n" + error.response.data.message)
                                setReferences(error.response.data?.references || [])
                            })
                        }}>{needDeleteConfirmation ? "Delete" : "Confirm"}</Button>
                    </Col>
                    <Col xs={10} className="d-flex flex-row justify-content-end">
                        <Button variant="theme" className="me-2" onClick={() => {
                            setModal("imageLibrary")
                            setReferences([])
                            setNeedDeleteConfirmation(true)
                        }}>Return</Button>
                        <Button variant="theme" onClick={() => {
                            navigator.clipboard.writeText(`<img src="/images/curriculum${selectedPath}${modal}" />`).then(() => {
                                setModal(null)
                                setReferences([])
                                setNeedDeleteConfirmation(true)
                                toast.info("Copied img tag!", { autoClose: 1000 })
                            })
                        }}>
                            Copy
                        </Button>
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    </>
}
